import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
} from 'recharts';
import { BuildingOfficeIcon, ShoppingBagIcon, DocumentTextIcon } from '@heroicons/react/24/outline';

/**
 * Charts Section Component for Business Permits
 * 
 * @param {Object} props
 * @param {Array} props.barangayData - Data for businesses per barangay
 * @param {Array} props.businessTypeData - Data for business type distribution
 * @param {Array} props.permitApplicationsData - Data for permit applications over time
 */
const ChartsSection = ({
  barangayData = [],
  businessTypeData = [],
  permitApplicationsData = {},
}) => {
  // Transform barangay data: {barangay: "Poblacion", total: 3} -> {name: "Poblacion", value: 3}
  const transformedBarangayData = Array.isArray(barangayData) 
    ? barangayData.map(item => ({
        name: item.barangay,
        value: item.total
      }))
    : [];

  // Transform business type data: {"Manufacturing": 66.67, "Retail": 33.33} -> [{name: "Manufacturing", value: 66.67}, ...]
  const transformedBusinessTypeData = typeof businessTypeData === 'object' && businessTypeData !== null
    ? Object.entries(businessTypeData).map(([name, value]) => ({
        name,
        value: typeof value === 'number' ? value : parseFloat(value) || 0
      }))
    : [];

  // Transform monthly applications data: {"2026-01": 1, "2026-02": 2} -> [{month: "2026-01", applications: 1}, ...]
  const transformedMonthlyData = typeof permitApplicationsData === 'object' && permitApplicationsData !== null
    ? Object.entries(permitApplicationsData)
        .sort(([a], [b]) => a.localeCompare(b)) // Sort by month
        .map(([month, applications]) => ({
          month,
          applications: typeof applications === 'number' ? applications : parseInt(applications) || 0
        }))
    : [];

  const barData = transformedBarangayData;
  const pieData = transformedBusinessTypeData;
  const lineData = transformedMonthlyData;

  const COLORS = ['#14B8A6', '#A78BFA', '#F59E0B', '#EC4899', '#3B82F6', '#10B981'];

  return (
    <div className="space-y-6">
      {/* Businesses per Barangay - Bar Chart */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-green-50 p-2 rounded-lg">
              <BuildingOfficeIcon className="h-5 w-5 text-green-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Businesses per barangay</h3>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={barData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="name"
              angle={-45}
              textAnchor="end"
              height={100}
              tick={{ fontSize: 12 }}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip />
            <Bar dataKey="value" fill="#14B8A6" radius={[8, 8, 0, 0]}>
              {barData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Business Type Distribution & Permit Applications - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Business Type Distribution - Donut Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-purple-50 p-2 rounded-lg">
                <ShoppingBagIcon className="h-5 w-5 text-purple-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Business Type Distribution</h3>
            </div>

          </div>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                label={({ percent }) => `${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            {pieData.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: item.color || COLORS[index % COLORS.length] }}
                />
                <span className="text-xs font-semibold text-gray-600">{item.name}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Permit Applications - Line Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="bg-orange-50 p-2 rounded-lg">
                <DocumentTextIcon className="h-5 w-5 text-orange-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Permit Applications</h3>
            </div>

          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={lineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="applications"
                stroke="#14B8A6"
                strokeWidth={3}
                dot={{ fill: '#14B8A6', r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ChartsSection;
